// /src/utils/perkThemeHalo.js
// Theme frame halo from effectConfig — soft glow, spiral, or frost (CSP via createDynamicCSS).

import { createDynamicCSS } from '../../utils/CreateDynamicCSS';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export const PERK_THEME_HALO_PICKER_DEFAULT = '#4a90e2';

export function sanitizePerkThemeHaloColor(value) {
  const hex = String(value || '').trim();
  return HEX_RE.test(hex) ? hex.toLowerCase() : null;
}

export function clampPerkThemeHaloSpiral(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function clampPerkThemeHaloFrozen(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Spiral and frozen are mutually exclusive — if both > 0, keep frozen and clear spiral.
 */
export function resolveExclusiveHaloMotion(spiralRaw, frozenRaw) {
  let spiral = clampPerkThemeHaloSpiral(spiralRaw);
  let frozen = clampPerkThemeHaloFrozen(frozenRaw);
  if (spiral > 0 && frozen > 0) {
    spiral = 0;
  }
  return { spiral, frozen };
}

/** Resolved halo state from effectConfig. */
export function resolvePerkThemeHalo(effectConfig) {
  const color = sanitizePerkThemeHaloColor(effectConfig?.haloColor);
  const { spiral, frozen } = resolveExclusiveHaloMotion(
    effectConfig?.haloSpiral,
    effectConfig?.haloFrozen,
  );
  return {
    enabled: Boolean(color),
    color,
    spiral,
    frozen,
  };
}

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Soft inset ~15px halo class for `.perk-theme-frame-halo`.
 * @param {string|null|undefined} haloColor
 * @param {{ opacity?: number }} [opts]
 */
export function getPerkThemeHaloClass(haloColor, opts = {}) {
  const color = sanitizePerkThemeHaloColor(haloColor);
  if (!color) return '';
  const opacity = Number.isFinite(opts.opacity) ? opts.opacity : 0.38;
  const opacityKey = Math.max(1, Math.min(100, Math.round(opacity * 100)));
  const suffix = color.replace('#', '');
  const className = `perk-theme-frame-halo-15s--${suffix}-o${opacityKey}`;
  const soft = hexToRgba(color, opacity);
  createDynamicCSS(className, {
    boxShadow: `inset 0 0 15px 0 ${soft}`,
  });
  return className;
}
